import { Injectable } from "@angular/core";
import {
  ApiStatus,
  EncounterApis,
  EncounterStatus,
  ProcedureRoomApis,
  ReferralApis,
  Utils,
} from "./utils";
import { EventService } from "./event.service";
import { ApplicationCache } from "../models/application-cache.model";
import { ProcedureRoomListing } from "../models/procedure-room.model";
import {
  BroadcastEventTypes,
  ChecklistQuestionsCache,
  HpiQuestions,
} from "../models/lookup.model";
import { NeurovascularAssessment } from "../models/neurovascular-assessment.model";
import {
  BloodGlucose,
  ChiefComplaint,
  DispenseSupply,
  EncounterSchedule,
  LeadEKG,
  Medication,
  ModifiedAldreteScoring,
  Procedure,
  SnacksAndMeals,
  Vitals,
} from "../models/procedure.model";
import { environment } from "src/environments/environment";
import { PatientDoc } from "../models/patient-docs.model";
import { Appointment, AppointmentChecklist } from "../models/admissions.model";
import {
  PriorAuth,
  getPriorAuthCptCodesFromList,
  normalizePriorAuth,
} from "../models/prior-auth.model";
import {
  deriveExpectedAuthRequirementStatusLabelFromCache,
  deriveExpectedAuthStatusLabelFromCache,
  isAuthRequiredForEncounterCpts,
} from "../helpers/encounter-auth-requirement.util";
import { Coverage, InsuranceType } from "../models/coverages.model";
import { Patient } from "../models/patient.model";
import { OldLabInfo } from "../models/old-labs.model";
import { ReferralChecklist } from "../models/referral-checklist.model";
import { ActivityNote } from "../models/activity-notes.model";
import { PreProcedureAssessment } from "../models/pre-procedure-assessment";
import { Allergy } from "../models/allergy.model";
import { AdministerMedication } from "../models/administer-medication.model";
import { AccessSite } from "../models/access-site.model";
import { Act } from "../models/act.model";

/** Auth workflow labels previously PATCHed to `encounter.encounterStatus`; use `authStatus` instead. */
const LEGACY_AUTH_LABELS_ON_ENCOUNTER_STATUS: ReadonlySet<string> = new Set([
  EncounterStatus.PendingAuth,
  EncounterStatus.NoAuthRequired,
  EncounterStatus.Authorized,
]);
import { ProcedureSummary } from "../models/procedure-summary.model";
import { ProcedureTimeout } from "../models/procedure-timeout.model";
import {
  CptCodesForEncounter,
  ICDCodesForEncounter,
} from "../models/cpt-codes.model";
import { Discharge } from "../models/discharge.model";
import { PhiProcedureMedicationRow } from "../models/phi-medications-reconciliation.model";
import {
  filterVisibleEncounterMedications,
  isCancelledEncounterMedication,
} from "../helpers/no-known-medications.util";
import {
  EncounterFamilyHistory,
  EncounterMedicalHistory,
  EncounterSocialHistory,
  EncounterSurgicalHistory,
  Problem,
  ProblemService,
  SoapNote,
} from "../models/problems.model";
import { Context } from "../models/contexts.model"
import { Task } from "../models/tasks.model"
import { HpiFindings, HpiSummary } from "../models/hpi-findings";
import { chain, cloneDeep, orderBy } from "lodash";
import { orderEncounterProblemsForDisplay } from "../utils/encounter-problems-order.util";
import { RosSummary } from "../models/ros-summary";

export type ApplicationCacheWriteTarget = "auto" | "main" | "preview";

@Injectable({
  providedIn: "root",
})
export class ApplicationCacheService {
  private _mainApplicationCache: ApplicationCache;
  /** Isolated cache for global search preview; main column reads {@link mainApplicationCache}. */
  private _searchPreviewOverlay: ApplicationCache | null = null;
  /**
   * When true, {@link applicationCache} reads/writes the main column (procedure room / worklist)
   * while global search preview uses {@link searchPreviewCache}.
   */
  private _pinMainColumnForSearchPreview = false;
  private apiStatuses: Map<string, ApiStatus>;

  /** Active encounter cache; during search preview this is the overlay, not the worklist main column. */
  get applicationCache(): ApplicationCache {
    if (this._pinMainColumnForSearchPreview && this._searchPreviewOverlay) {
      return this._mainApplicationCache;
    }
    return this._searchPreviewOverlay ?? this._mainApplicationCache;
  }

  set applicationCache(value: ApplicationCache) {
    this._mainApplicationCache = value;
    this._searchPreviewOverlay = null;
  }

  /** Worklist / Engage main column — never the search preview overlay. */
  get mainApplicationCache(): ApplicationCache {
    return this._mainApplicationCache;
  }

  get isSearchPreviewOverlayActive(): boolean {
    return this._searchPreviewOverlay != null;
  }

  /** Global search preview modal — isolated from pinned main column when active. */
  get searchPreviewCache(): ApplicationCache {
    return this._searchPreviewOverlay ?? this._mainApplicationCache;
  }

  constructor(private readonly _eventService: EventService) {
    this.clearSelection();
  }

  /**
   * Open search preview without disturbing the visible main column (worklist prospect or procedure room).
   */
  beginMainColumnIsolationForSearchPreview(): void {
    this._pinMainColumnForSearchPreview = true;
    this.beginSearchPreviewOverlaySession();
  }

  beginSearchPreviewOverlaySession(): void {
    this._searchPreviewOverlay = new ApplicationCache();
    /** Main-column API success flags must not skip fetches into the empty overlay cache. */
    this.setApiStatus(ApiStatus.Pending, ReferralApis.Patient);
    this.setApiStatus(ApiStatus.Pending, ReferralApis.Coverages);
    this.setApiStatus(ApiStatus.Pending, ReferralApis.Allergies);
    this.setApiStatus(ApiStatus.Pending, ReferralApis.Documents);
    this.setApiStatus(ApiStatus.Pending, ProcedureRoomApis.MedicationReconciliation);
  }

  endSearchPreviewOverlaySession(options?: { promoteToMain?: boolean }): void {
    const overlay = this._searchPreviewOverlay;
    this._searchPreviewOverlay = null;
    this._pinMainColumnForSearchPreview = false;
    if (!options?.promoteToMain || !overlay) {
      return;
    }
    this._mainApplicationCache = cloneDeep(overlay);
    const appt = this._mainApplicationCache.appointment;
    if (appt?.id) {
      this._eventService.broadcast(BroadcastEventTypes.AppointmentUpdated, appt);
    }
    if (this._mainApplicationCache.patient?.id) {
      this._eventService.broadcast(
        BroadcastEventTypes.PatientInfoUpdated,
        this._mainApplicationCache.patient
      );
    }
    this._eventService.broadcast(BroadcastEventTypes.EncounterMedicationsUpdated);
    this._eventService.broadcast(BroadcastEventTypes.AllergiesUpdated);
    this._eventService.broadcast(BroadcastEventTypes.PatientDocsLoaded);
  }

  private shouldBroadcastCacheEvents(): boolean {
    return !this._searchPreviewOverlay;
  }

  /**
   * During search preview, cache writes go to the overlay only — broadcast a scoped refresh so
   * modal children re-read {@link applicationCache} without notifying the worklist main column.
   */
  private broadcastCacheChange(type: BroadcastEventTypes, payload?: unknown): void {
    if (this.shouldBroadcastCacheEvents()) {
      this._eventService.broadcast(type, payload);
      return;
    }
    if (this._searchPreviewOverlay) {
      this._eventService.broadcast(BroadcastEventTypes.SearchPreviewCacheUpdated);
    }
  }

  private resolveWriteCache(
    forceMain = false,
    writeTarget: ApplicationCacheWriteTarget = "auto"
  ): ApplicationCache {
    if (writeTarget === "main" || forceMain) {
      return this._mainApplicationCache;
    }
    if (writeTarget === "preview" && this._searchPreviewOverlay) {
      return this._searchPreviewOverlay;
    }
    if (this._pinMainColumnForSearchPreview) {
      return this._mainApplicationCache;
    }
    if (this._searchPreviewOverlay) {
      return this._searchPreviewOverlay;
    }
    return this._mainApplicationCache;
  }

  setApiStatus(status: ApiStatus, api: string) {
    this.apiStatuses.set(api, status);
  }

  getApiStatus(api: string): ApiStatus {
    return this.apiStatuses.get(api);
  }

  setAllApisStatus(status: ApiStatus) {
    for (let key of this.apiStatuses.keys()) {
      this.setApiStatus(status, key);
    }
  }

  setDocumentCache(
    docs: PatientDoc[],
    options?: { writeTarget?: ApplicationCacheWriteTarget },
  ) {
    const cache = this.resolveWriteCache(false, options?.writeTarget ?? "auto");
    cache.documents = docs;
    this.broadcastCacheChange(BroadcastEventTypes.PatientDocsLoaded);
  }

  clearDocumentCache() {
    this.applicationCache.documents = [];
    this.setApiStatus(ApiStatus.Pending, ReferralApis.Documents);
  }

  addDocumentToCache(doc: PatientDoc) {
    const id = String(doc?.id ?? "").trim();
    const existing = this.applicationCache.documents;
    if (!id) {
      this.applicationCache.documents = [...existing, doc];
    } else {
      const idx = existing.findIndex((d) => d.id === id);
      if (idx >= 0) {
        const next = [...existing];
        next[idx] = { ...next[idx], ...doc };
        this.applicationCache.documents = next;
      } else {
        this.applicationCache.documents = [...existing, doc];
      }
    }
    this.broadcastCacheChange(BroadcastEventTypes.PatientDocsLoaded);
  }

  removePatientDocFromCache(docId: string) {
    this.applicationCache.documents = this.applicationCache.documents.filter(
      (doc) => doc.id !== docId
    );
  }

  /** Merge server-saved metadata onto a cached document (e.g. after OCR PATCH). */
  mergeDocumentMetadataInCache(
    documentId: string,
    metadata: Record<string, unknown>
  ): void {
    const id = String(documentId ?? "").trim();
    if (!id) {
      return;
    }
    const docs = this.applicationCache.documents;
    const idx = docs.findIndex((d) => d.id === id);
    if (idx < 0) {
      return;
    }
    const next = [...docs];
    next[idx] = { ...next[idx], metadata };
    this.applicationCache.documents = next;
  }

  updateScheduleCache(
    schedule: EncounterSchedule,
    options?: { writeTarget?: ApplicationCacheWriteTarget }
  ) {
    const cache = this.resolveWriteCache(false, options?.writeTarget ?? "auto");
    cache.schedule = schedule;
    this.broadcastCacheChange(BroadcastEventTypes.ScheduleCacheUpdated);
  }

  updateProcedureCache(
    procedure: Procedure,
    options?: { writeTarget?: ApplicationCacheWriteTarget }
  ) {
    const cache = this.resolveWriteCache(false, options?.writeTarget ?? "auto");
    cache.procedure = procedure;
    this.broadcastCacheChange(BroadcastEventTypes.ProcedureCacheUpdated, {
      ...procedure,
    });
  }

  /** Set when Engage selects a lead without patientId; cleared when a clinic patient is selected. */
  setEngageNotesReferralId(referralId: string | null | undefined): void {
    const cache = this.resolveWriteCache(true);
    const v = (referralId || '').trim();
    cache.engageNotesReferralId = v || null;
    cache.engageReferralAssignedToUserId = null;
    if (this.shouldBroadcastCacheEvents()) {
      this._eventService.broadcast(BroadcastEventTypes.EngageReferralAssignmentChanged);
    }
  }

  /** Engage Patients: sidebar header title while Global Notes resolves patient from referral / patient fetch. */
  setEngageProspectPatientDisplayName(name: string | null | undefined): void {
    const cache = this.resolveWriteCache(true);
    const v = String(name ?? "")
      .trim()
      .replace(/\s+/g, " ");
    cache.engageProspectPatientDisplayName = v || null;
  }

  /** `userId` when the referral is assigned to a user; `null` when unassigned (everyone). */
  setEngageReferralAssignedToUserId(userId: string | null | undefined): void {
    const cache = this.resolveWriteCache();
    const u = (userId || '').trim();
    const next = u || null;
    if (cache.engageReferralAssignedToUserId === next) {
      return;
    }
    cache.engageReferralAssignedToUserId = next;
    if (this.shouldBroadcastCacheEvents()) {
      this._eventService.broadcast(BroadcastEventTypes.EngageReferralAssignmentChanged);
    } else if (this._searchPreviewOverlay) {
      this._eventService.broadcast(BroadcastEventTypes.SearchPreviewCacheUpdated);
    }
  }

  updateAppointmentCache(
    updatedAppointment: Appointment,
    options?: { writeTarget?: ApplicationCacheWriteTarget }
  ) {
    const cache = this.resolveWriteCache(false, options?.writeTarget ?? "auto");
    const previousEncounterId = cache.appointment?.id;
    const previousAppointment = cache.appointment;
    const sameEncounter =
      !!previousAppointment?.id &&
      !!updatedAppointment?.id &&
      previousAppointment.id === updatedAppointment.id;
    cache.appointment = sameEncounter
      ? this.mergeSameEncounterAppointment(previousAppointment, updatedAppointment)
      : updatedAppointment;
    // Mark context APIs pending when switching encounters; keep prior lists until refetch completes.
    if (previousEncounterId && previousEncounterId !== updatedAppointment?.id) {
      this.setApiStatus(ApiStatus.Pending, EncounterApis.Contexts);
      if (cache === this._mainApplicationCache) {
        this.markProcedureRoomContextsLoading();
        this.clearPriorAuthCache();
      }
    }
    this.broadcastCacheChange(
      BroadcastEventTypes.AppointmentUpdated,
      cache.appointment
    );
  }

  /**
   * PATCH / partial encounter payloads often omit scheduling metadata. Preserve those fields
   * when refreshing the same encounter in cache (e.g. after status-only updates).
   */
  private mergeSameEncounterAppointment(
    previous: Appointment,
    incoming: Appointment
  ): Appointment {
    const merged: Appointment = { ...previous, ...incoming };

    const preserveWhenIncomingEmpty = (
      field: keyof Pick<
        Appointment,
        | "service"
        | "referralSource"
        | "referralSourceId"
        | "reasonForVisit"
        | "reasonOfVisit"
      >
    ): void => {
      const next = (incoming[field] ?? "").toString().trim();
      const prev = (previous[field] ?? "").toString().trim();
      if (!next && prev) {
        merged[field] = previous[field];
      }
    };

    preserveWhenIncomingEmpty("service");
    preserveWhenIncomingEmpty("referralSource");
    preserveWhenIncomingEmpty("referralSourceId");
    preserveWhenIncomingEmpty("reasonForVisit");
    preserveWhenIncomingEmpty("reasonOfVisit");

    if (!incoming.encounterSchedule && previous.encounterSchedule) {
      merged.encounterSchedule = previous.encounterSchedule;
    }
    if (!incoming.encounterProcedure && previous.encounterProcedure) {
      merged.encounterProcedure = previous.encounterProcedure;
    }
    if (!incoming.encountersPatient && previous.encountersPatient) {
      merged.encountersPatient = previous.encountersPatient;
    }
    if (
      !Utils.readNewOrEstablishedStatus(incoming) &&
      Utils.readNewOrEstablishedStatus(previous)
    ) {
      merged.newOrEstablishedStatus = previous.newOrEstablishedStatus;
    }
    const incomingDisplayNumber = (incoming.displayNumber ?? "").toString().trim();
    const previousDisplayNumber = (previous.displayNumber ?? "").toString().trim();
    if (!incomingDisplayNumber && previousDisplayNumber) {
      merged.displayNumber = previous.displayNumber;
    }

    const preserveTrimmedStringField = (
      field: keyof Pick<
        Appointment,
        | "claimStatus"
        | "clinicalStatus"
        | "billingStatus"
        | "signReqStatus"
        | "authStatus"
        | "authRequirementStatus"
        | "primaryPayerName"
        | "eligibility"
        | "authorizationStatus"
      >
    ): void => {
      const next = (incoming[field] ?? "").toString().trim();
      const prev = (previous[field] ?? "").toString().trim();
      if (!next && prev) {
        merged[field] = previous[field];
      }
    };

    preserveTrimmedStringField("claimStatus");
    preserveTrimmedStringField("clinicalStatus");
    preserveTrimmedStringField("billingStatus");
    preserveTrimmedStringField("signReqStatus");
    preserveTrimmedStringField("authStatus");
    preserveTrimmedStringField("authRequirementStatus");
    preserveTrimmedStringField("primaryPayerName");
    preserveTrimmedStringField("eligibility");
    preserveTrimmedStringField("authorizationStatus");

    return merged;
  }

  clearPriorAuthCache(): void {
    this.applicationCache.priorAuths = [];
    this.applicationCache.priorAuth = undefined;
    this.setApiStatus(ApiStatus.Pending, ReferralApis.PriorAuth);
  }

  updatePriorAuthsCache(priorAuths: PriorAuth[]) {
    this.applicationCache.priorAuths = priorAuths
      .map((auth) => normalizePriorAuth(auth))
      .filter((auth): auth is PriorAuth => !!auth);
    this.applicationCache.priorAuth =
      this.applicationCache.priorAuths[0] ?? undefined;
    this._eventService.broadcast(
      BroadcastEventTypes.PriorAuthUpdated,
      this.applicationCache.priorAuth
    );
  }

  updatePriorAuthCache(updatedPriorAuth: PriorAuth) {
    const normalized =
      normalizePriorAuth(updatedPriorAuth) ?? updatedPriorAuth;
    if (!this.applicationCache.priorAuths) {
      this.applicationCache.priorAuths = [];
    }
    const existingIndex = normalized.id
      ? this.applicationCache.priorAuths.findIndex(
          (auth) => auth.id === normalized.id
        )
      : -1;
    if (existingIndex >= 0) {
      this.applicationCache.priorAuths[existingIndex] = normalized;
    } else {
      this.applicationCache.priorAuths.push(normalized);
    }
    this.applicationCache.priorAuth = normalized;
    this._eventService.broadcast(
      BroadcastEventTypes.PriorAuthUpdated,
      normalized
    );
  }

  updateCoverageCache(
    updatedCoverages: Coverage[],
    options?: { writeTarget?: ApplicationCacheWriteTarget }
  ) {
    const cache = this.resolveWriteCache(false, options?.writeTarget ?? "auto");
    cache.coverages = updatedCoverages;
    this.broadcastCacheChange(
      BroadcastEventTypes.CoverageUpdated,
      updatedCoverages
    );
  }

  updatePatientInfoCache(
    patient: Patient,
    options?: { writeTarget?: ApplicationCacheWriteTarget }
  ) {
    const cache = this.resolveWriteCache(false, options?.writeTarget ?? "auto");
    const previous = cache.patient;
    cache.patient = {
      ...(previous || {}),
      ...patient,
      id: (patient?.id || previous?.id || "").trim() || previous?.id,
    };
    const mergedPatient = cache.patient;
    const appt = cache.appointment;
    if (appt?.encountersPatient && mergedPatient?.id) {
      const ep = appt.encountersPatient;
      const matchesEncounter =
        ep.id === mergedPatient.id || appt.patientId === mergedPatient.id;
      if (matchesEncounter) {
        const nameParts = [
          mergedPatient.firstName,
          mergedPatient.middleName,
          mergedPatient.lastName,
        ]
          .filter((x) => x != null && String(x).trim() !== "")
          .map((x) => String(x).trim());
        if (nameParts.length > 0) {
          ep.name = nameParts.join(" ");
        }
        if (mergedPatient.dob) {
          ep.dob = mergedPatient.dob;
        }
        if (
          mergedPatient.gender != null &&
          String(mergedPatient.gender).trim() !== ""
        ) {
          ep.gender = mergedPatient.gender as string;
        }
      }
    }
    this.broadcastCacheChange(
      BroadcastEventTypes.PatientInfoUpdated,
      mergedPatient
    );
  }

  setChiefComplaintCache(updatedChiefComplaint: ChiefComplaint) {
    this.applicationCache.chiefComplaint = updatedChiefComplaint;
    this._eventService.broadcast(
      BroadcastEventTypes.ChiefComplaintUpdated,
      updatedChiefComplaint
    );
  }

  setFollowUpChiefComplaintCache(updatedChiefComplaint: ChiefComplaint) {
    this.applicationCache.followUpChiefComplaint = updatedChiefComplaint;
    this._eventService.broadcast(
      BroadcastEventTypes.ChiefComplaintUpdated,
      updatedChiefComplaint
    );
  }

  setChecklistQuestionsCache(questions: ChecklistQuestionsCache[]) {
    this.applicationCache.checklistQuestions = questions;
    this._eventService.broadcast(BroadcastEventTypes.ChecklistQuestionsUpdated);
  }

  setHpiQuestionsCache(questions: HpiQuestions[]) {
    this.applicationCache.hpiQuestions = questions;
    this._eventService.broadcast(BroadcastEventTypes.HpiQuestionsAdded);
  }

  updateHpiQuestionsCache(questions: HpiQuestions[]) {
    questions.forEach((question: HpiQuestions) => {
      const matchingRecord = this.applicationCache.hpiQuestions.find(
        (x) => x.id === question.id
      );

      if (matchingRecord) {
        Object.assign(matchingRecord, question);
      } else {
        this.applicationCache.hpiQuestions.push({ ...question });
      }
    });
    this._eventService.broadcast(BroadcastEventTypes.HpiQuestionsUpdated);
  }

  /**
   * Replace all questions of the given type with the new list (e.g. after a newVersion save).
   * Prevents duplicate questions when the API returns new ids for a new version.
   */
  replaceHpiQuestionsByType(type: string, questions: HpiQuestions[]): void {
    const current = this.applicationCache.hpiQuestions || [];
    const rest = current.filter((q: HpiQuestions) => (q as any).type !== type);
    this.applicationCache.hpiQuestions = [...rest, ...questions.map((q) => ({ ...q }))];
    this._eventService.broadcast(BroadcastEventTypes.HpiQuestionsUpdated);
  }

  setHpiSymptomsCache(symptoms: any[]) {
    this.applicationCache.hpiSymptoms = symptoms;
    this._eventService.broadcast(BroadcastEventTypes.HpiSymptomsUpdated);
  }

  updateHpiSymptomsCache(symptoms: any[]) {
    this.applicationCache.hpiSymptoms = symptoms;
    this._eventService.broadcast(BroadcastEventTypes.HpiSymptomsUpdated);
  }

  setRosQuestionsCache(questions: HpiQuestions[]) {
    this.applicationCache.rosQuestions = questions;
    this._eventService.broadcast(BroadcastEventTypes.RosQuestionsAdded);
  }

  updateRosQuestionsCache(questions: HpiQuestions[]) {
    questions.forEach((question: HpiQuestions) => {
      const matchingRecord = this.applicationCache.rosQuestions.find(
        (x) => x.id === question.id
      );

      if (matchingRecord) {
        Object.assign(matchingRecord, question);
      } else {
        this.applicationCache.rosQuestions.push({ ...question });
      }
    });
    this._eventService.broadcast(BroadcastEventTypes.RosQuestionsUpdated);
  }

  setRosSymptomsCache(symptoms: any[]) {
    this.applicationCache.rosSymptoms = symptoms;
    this._eventService.broadcast(BroadcastEventTypes.RosSymptomsUpdated);
  }

  updateRosSymptomsCache(symptoms: any[]) {
    this.applicationCache.rosSymptoms = symptoms;
    this._eventService.broadcast(BroadcastEventTypes.RosSymptomsUpdated);
  }

  clearHpiSymptomsCache() {
    this.applicationCache.hpiSymptoms = [];
    this._eventService.broadcast(BroadcastEventTypes.HpiSymptomsUpdated);
  }

  setHpiSummaryCache(findings: HpiFindings) {
    this.applicationCache.hpiSummary = findings;
    this._eventService.broadcast(BroadcastEventTypes.HpiFindingsUpdated);
  }

  setLabInfoCache(labs: OldLabInfo[]) {
    this.applicationCache.labs = labs || [];
    this._eventService.broadcast(BroadcastEventTypes.LabsInfoUpdated);
  }

  updateLabInfoCache(labInfo: OldLabInfo) {
    const matchingLabInfo = this.applicationCache.labs.find(
      (x) => x.id === labInfo.id
    );
    if (!matchingLabInfo) {
      this.applicationCache.labs = [...this.applicationCache.labs, labInfo];
    } else {
      Object.assign(matchingLabInfo, labInfo);
    }
    this._eventService.broadcast(BroadcastEventTypes.LabsInfoUpdated);
  }

  updateAppointmentChecklistFlags(checklist: AppointmentChecklist) {
    this.applicationCache.checklistFlags = checklist;
  }

  updateAppointmentChecklistValues(checklist: ReferralChecklist) {
    this.applicationCache.checklistValues = checklist;
  }

  broadcastProgressUpdate() {
    this._eventService.broadcast(BroadcastEventTypes.RequiresProgressUpdate);
  }

  /**
   * Legacy hook used after coverage / prior-auth saves to PATCH `encounter.encounterStatus`.
   * Auth workflow labels must not be written there — use {@link getExpectedAuthStatusLabel}
   * when we enable `encounter.authStatus` sync instead.
   */
  getExpectedAppointmentStatus(): string {
    const isScheduled = !!this.applicationCache.schedule?.id;
    const current = (this.applicationCache.appointment?.encounterStatus ?? "")
      .trim();
    if (
      current &&
      !LEGACY_AUTH_LABELS_ON_ENCOUNTER_STATUS.has(current)
    ) {
      return current;
    }
    return isScheduled ? EncounterStatus.Scheduled : EncounterStatus.NewPatient;
  }

  /**
   * Coverage + prior-auth derived auth workflow label for PHI `encounter_status.authStatus`.
   */
  getExpectedAuthStatusLabel(): string | null {
    return deriveExpectedAuthStatusLabelFromCache(this.applicationCache);
  }

  /** PHI `encounter_status.authRequirementStatus` for the open encounter (per DOS). */
  getExpectedAuthRequirementStatusLabel(): string | null {
    return deriveExpectedAuthRequirementStatusLabelFromCache(
      this.applicationCache
    );
  }

  setProcedureRoomListingCache(procedureListing: ProcedureRoomListing) {
    this.applicationCache.procedureListing = procedureListing;
    this._eventService.broadcast(
      BroadcastEventTypes.ProcedureRoomListingUpdated
    );
  }

  setNeurovascularAssessmentCache(neuro: NeurovascularAssessment[]) {
    this.applicationCache.neurovascularAssessment = neuro;
    this._eventService.broadcast(
      BroadcastEventTypes.NeurovascularAssessmentUpdated
    );
  }

  setSnacksAndMealsCache(snacks: SnacksAndMeals[]) {
    this.applicationCache.snacksAndMeals = snacks;
    this._eventService.broadcast(BroadcastEventTypes.SnacksAndMealsUpdated);
  }

  updateSnacksAndMealsCache(snacks: SnacksAndMeals) {
    const matchingRecord = this.applicationCache.snacksAndMeals.find(
      (x) => x.id === snacks.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, snacks);
    } else {
      this.applicationCache.snacksAndMeals.push({ ...snacks });
    }
    this._eventService.broadcast(BroadcastEventTypes.SnacksAndMealsUpdated);
  }

  setDispenseSuppliesCache(supplies: DispenseSupply[]) {
    this.applicationCache.dispenseSupplies = supplies;
    this._eventService.broadcast(BroadcastEventTypes.DispenseSuppliesUpdated);
  }

  updateDispenseSuppliesCache(supply: DispenseSupply) {
    const matchingRecord = this.applicationCache.dispenseSupplies.find(
      (x) => x.id === supply.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, supply);
    } else {
      this.applicationCache.dispenseSupplies.push({ ...supply });
    }
    this._eventService.broadcast(BroadcastEventTypes.SnacksAndMealsUpdated);
  }

  setVitalsCache(vitals: Vitals[]) {
    const cache = this.resolveWriteCache();
    cache.vitals = vitals;
    if (this.shouldBroadcastCacheEvents()) {
      this._eventService.broadcast(BroadcastEventTypes.VitalsUpdated);
    }
  }

  updateVitalsCache(vitals: Vitals) {
    const matchingRecord = this.applicationCache.vitals.find(
      (x) => x.id === vitals.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, vitals);
    } else {
      this.applicationCache.vitals.unshift({ ...vitals });
    }
    this._eventService.broadcast(BroadcastEventTypes.VitalsUpdated);
  }

  setAllergiesCache(
    allergies: Allergy[],
    options?: { writeTarget?: ApplicationCacheWriteTarget },
  ) {
    const cache = this.resolveWriteCache(false, options?.writeTarget ?? "auto");
    cache.allergies = allergies;
    this.broadcastCacheChange(BroadcastEventTypes.AllergiesUpdated);
  }

  updateAllergiesCache(allergy: Allergy, broadcast: boolean) {
    const matchingRecord = this.applicationCache.allergies.find(
      (x) => x.id === allergy.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, allergy);
    } else {
      this.applicationCache.allergies.push({ ...allergy });
    }
    if (!broadcast) {
      return;
    }
    this.broadcastCacheChange(BroadcastEventTypes.AllergiesUpdated);
  }

  broadcastAllergiesUpdated() {
    this._eventService.broadcast(BroadcastEventTypes.AllergiesUpdated);
  }

  setBloodGlucoseCache(bloodGlucose: BloodGlucose[]) {
    this.applicationCache.bloodGlucose = bloodGlucose;
    this._eventService.broadcast(BroadcastEventTypes.BloodGlucoseUpdated);
  }

  updateBloodGlucoseCache(bloodGlucose: BloodGlucose) {
    const matchingRecord = this.applicationCache.bloodGlucose.find(
      (x) => x.id === bloodGlucose.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, bloodGlucose);
    } else {
      this.applicationCache.bloodGlucose.push({ ...bloodGlucose });
    }
    this._eventService.broadcast(BroadcastEventTypes.BloodGlucoseUpdated);
  }

  setModifiedAldreteScoringCache(
    modifiedAldreteScoring: ModifiedAldreteScoring[]
  ) {
    this.applicationCache.modifiedAldreteScoring = modifiedAldreteScoring;
    this._eventService.broadcast(
      BroadcastEventTypes.ModifiedAldreteScoringUpdated
    );
  }

  updateModifiedAldreteScoringCache(
    modifiedAldreteScoring: ModifiedAldreteScoring
  ) {
    const matchingRecord = this.applicationCache.modifiedAldreteScoring.find(
      (x) => x.id === modifiedAldreteScoring.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, modifiedAldreteScoring);
    } else {
      this.applicationCache.modifiedAldreteScoring.push({
        ...modifiedAldreteScoring,
      });
    }
    this._eventService.broadcast(
      BroadcastEventTypes.ModifiedAldreteScoringUpdated
    );
  }

  setLeadEKGCache(leadEKG: LeadEKG[]) {
    this.applicationCache.leadEKG = leadEKG;
    this._eventService.broadcast(BroadcastEventTypes.LeadEKGUpdated);
  }

  updateLeadEKGCache(leadEKG: LeadEKG) {
    const matchingRecord = this.applicationCache.leadEKG.find(
      (x) => x.id === leadEKG.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, leadEKG);
    } else {
      this.applicationCache.leadEKG.push({ ...leadEKG });
    }
    this._eventService.broadcast(BroadcastEventTypes.LeadEKGUpdated);
  }

  setMedicationReconciliationCache(
    medicationReconciliation: Medication[],
    options?: { writeTarget?: ApplicationCacheWriteTarget },
  ) {
    const cache = this.resolveWriteCache(false, options?.writeTarget ?? "auto");
    cache.medicationReconciliation = medicationReconciliation;
    this.broadcastCacheChange(BroadcastEventTypes.MedicationReconciliationUpdated);
  }

  setProcedurePhiMedicationsCache(
    procedurePhiMedications: PhiProcedureMedicationRow[]
  ) {
    this.applicationCache.procedurePhiMedications = procedurePhiMedications;
    this._eventService.broadcast(
      BroadcastEventTypes.ProcedurePhiMedicationsUpdated
    );
  }

  updateMedicationReconciliationCache(medication: Medication) {
    const matchingRecord = this.applicationCache.medicationReconciliation.find(
      (x) => x.id === medication.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, medication);
    } else {
      this.applicationCache.medicationReconciliation.unshift({ ...medication });
    }
    this._eventService.broadcast(
      BroadcastEventTypes.MedicationReconciliationUpdated
    );
  }

  setAdministerMedsCache(administerMeds: AdministerMedication[]) {
    this.applicationCache.administerMeds = administerMeds;
    this._eventService.broadcast(BroadcastEventTypes.AdministerMedsUpdated);
  }

  updateAdministerMedsCache(medication: AdministerMedication) {
    const matchingRecord = this.applicationCache.administerMeds.find(
      (x) => x.id === medication.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, medication);
    } else {
      this.applicationCache.administerMeds.unshift({ ...medication });
    }
    this._eventService.broadcast(BroadcastEventTypes.AdministerMedsUpdated);
  }

  setAccessSiteCache(accessSite: AccessSite[]) {
    this.applicationCache.accessSites = accessSite;
    this._eventService.broadcast(BroadcastEventTypes.AccessSiteUpdated);
  }

  setCPTCodeCache(cpts: CptCodesForEncounter[]) {
    const requiresAuth = isAuthRequiredForEncounterCpts(this.applicationCache);
    cpts.forEach((cpt) => {
      cpt.isAuthorized =
        !requiresAuth ||
        cpt.type === "Auto" ||
        this.authroizeCPTCodes.includes(cpt.cptCode);
    });
    this.applicationCache.cptCodes = cpts;
    this._eventService.broadcast(BroadcastEventTypes.CptCodesUpdated);
  }

  private get authroizeCPTCodes(): string[] {
    if (this.applicationCache.priorAuths?.length) {
      return getPriorAuthCptCodesFromList(this.applicationCache.priorAuths);
    }
    return getPriorAuthCptCodesFromList(
      this.applicationCache.priorAuth
        ? [this.applicationCache.priorAuth]
        : []
    );
  }

  setICDCodeCache(icds: ICDCodesForEncounter[]) {
    this.applicationCache.icdCodes = icds;
    this._eventService.broadcast(BroadcastEventTypes.IcdCodesUpdated);
  }

  updateAccessSiteCache(accessSite: AccessSite) {
    const matchingRecord = this.applicationCache.accessSites.find(
      (x) => x.id === accessSite.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, accessSite);
    } else {
      this.applicationCache.accessSites.unshift({ ...accessSite });
    }
    this._eventService.broadcast(BroadcastEventTypes.AccessSiteUpdated);
  }

  setActsCache(acts: Act[]) {
    this.applicationCache.acts = acts;
    this._eventService.broadcast(BroadcastEventTypes.ActsUpdated);
  }

  updateActsCache(act: Act) {
    const matchingRecord = this.applicationCache.acts.find(
      (x) => x.id === act.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, act);
    } else {
      this.applicationCache.acts.push({ ...act });
    }
    this._eventService.broadcast(BroadcastEventTypes.ActsUpdated);
  }

  setPreProcedureAssessmentCache(
    preProcedureAssessment: PreProcedureAssessment
  ) {
    this.applicationCache.preProcedureAssessment = preProcedureAssessment;
    this._eventService.broadcast(
      BroadcastEventTypes.PreProcedureAssessmentUpdated
    );
  }

  setProcedureRoomNurseActivityNotesCache(notes: ActivityNote[]): void {
    this.applicationCache.procedureRoomNurseActivityNotes = [...(notes || [])];
    this._eventService.broadcast(
      BroadcastEventTypes.ProcedureRoomNurseActivityNotesUpdated
    );
  }

  setProcedureRoomActivityNotesCache(notes: ActivityNote[]): void {
    this.applicationCache.procedureRoomActivityNotes = [...(notes || [])];
  }

  upsertProcedureRoomActivityNoteCache(note: ActivityNote): void {
    if (!note?.id) {
      return;
    }
    const existing = [...(this.applicationCache.procedureRoomActivityNotes || [])];
    const index = existing.findIndex((item) => item.id === note.id);
    if (index >= 0) {
      existing[index] = note;
    } else {
      existing.push(note);
    }
    this.setProcedureRoomActivityNotesCache(existing);
  }

  upsertProcedureRoomNurseActivityNoteCache(note: ActivityNote): void {
    if (!note?.id) {
      return;
    }
    const existing = [
      ...(this.applicationCache.procedureRoomNurseActivityNotes || []),
    ];
    const index = existing.findIndex((item) => item.id === note.id);
    if (index >= 0) {
      existing[index] = note;
    } else {
      existing.push(note);
    }
    this.setProcedureRoomNurseActivityNotesCache(existing);
  }

  setProcedureSummaryCache(procedureSummary: ProcedureSummary) {
    this.applicationCache.procedureSummary = procedureSummary;
    this._eventService.broadcast(BroadcastEventTypes.ProcedureSummaryUpdated);
  }

  setProcedureTimeoutCache(procedureTimeout: ProcedureTimeout) {
    this.applicationCache.procedureTimeout = procedureTimeout;
    const listing = this.applicationCache.procedureListing;
    const timeoutProcedureId = String(procedureTimeout?.procedureId ?? "").trim();
    const timeoutValue = String(procedureTimeout?.timeout ?? "").trim();
    if (
      listing &&
      timeoutProcedureId &&
      String(listing.id ?? "").trim() === timeoutProcedureId
    ) {
      listing.procedureTimeout = timeoutValue;
    }
    this._eventService.broadcast(BroadcastEventTypes.ProcedureTimeoutUpdated);
  }

  setDischargeCache(discharge: Discharge) {
    this.applicationCache.discharge = discharge;
    this._eventService.broadcast(BroadcastEventTypes.DischargeUpdated);
  }

  setPreAnesthsiaSummaryCache(preAnesthsiaSummary: HpiSummary) {
    this.applicationCache.preAnesthsiaSummary = preAnesthsiaSummary;
    this._eventService.broadcast(
      BroadcastEventTypes.PreAnesthsiaSummaryUpdated
    );
  }

  updateHPISummaryCache(hpi: HpiFindings) {
    this.applicationCache.preAnesthsiaSummary.hpi = hpi;
    this._eventService.broadcast(
      BroadcastEventTypes.PreAnesthsiaSummaryUpdated,
      this.applicationCache.preAnesthsiaSummary
    );
  }

  //clinic v3
  setProblemCache(problems: Problem[]) {
    this.applicationCache.encounterProblems = problems;
    this.setAlphabetsToProblemCache();
    this.broadcastProblemsUpdated(false);
  }

  setContextCache(contexts: Context[], encounterId?: string) {
    const currentEncounterId = (this.applicationCache.appointment?.id ?? "").trim();
    const targetEncounterId = (encounterId ?? "").trim();
    if (
      targetEncounterId &&
      currentEncounterId &&
      targetEncounterId !== currentEncounterId
    ) {
      return;
    }
    this.applicationCache.encounterContexts = contexts;
    this.broadcastContextsUpdated(encounterId);
  }

  markProcedureRoomContextsLoading(): void {
    this.setApiStatus(ApiStatus.Pending, ProcedureRoomApis.ProcedureRoomContexts);
    this._eventService.broadcast(BroadcastEventTypes.ProcedureRoomContextsUpdated);
  }

  setProcedureRoomContextCache(contexts: Context[]) {
    this.applicationCache.procedureRoomContexts = contexts || [];
    this.setApiStatus(ApiStatus.Success, ProcedureRoomApis.ProcedureRoomContexts);
    this._eventService.broadcast(BroadcastEventTypes.ProcedureRoomContextsUpdated);
  }

  private setAlphabetsToProblemCache() {
    const currentEncounterId = this.applicationCache.appointment?.id ?? "";
    this.applicationCache.encounterProblems = orderEncounterProblemsForDisplay(
      this.applicationCache.encounterProblems,
      currentEncounterId,
    );
  }

  updateProblemCache(problem: Problem, broadcast: boolean) {
    const matchingRecord = this.applicationCache.encounterProblems.find(
      (x) => x.id === problem.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, problem);
    } else {
      this.applicationCache.encounterProblems.push({ ...problem });
    }
    if (!broadcast) {
      return;
    }
    this.setAlphabetsToProblemCache(); //only call this while inserting new record.
    this.broadcastProblemsUpdated(false);
  }

  broadcastProblemsUpdated(setAlphabets: boolean) {
    if (setAlphabets) {
      this.setAlphabetsToProblemCache();
    }
    this._eventService.broadcast(BroadcastEventTypes.EncounterProblemsUpdated);
  }

  setProblemServiceCache(
    problemServices: ProblemService[],
    autoGeneratedServices: boolean = false
  ) {
    this.applicationCache.encounterServices = problemServices;
    if (autoGeneratedServices) {
      this._eventService.broadcast(
        BroadcastEventTypes.EncounterServicesAutoGeneratedUpdated
      );
      return;
    }
    this._eventService.broadcast(BroadcastEventTypes.EncounterServicesUpdated);
  }

  updateProblemServiceCache(
    problemService: ProblemService,
    broadcast: boolean
  ) {
    const matchingRecord = this.applicationCache.encounterServices.find(
      (x) => x.id === problemService.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, problemService);
    } else {
      this.applicationCache.encounterServices.unshift({ ...problemService });
    }
    if (!broadcast) {
      return;
    }
    this._eventService.broadcast(BroadcastEventTypes.EncounterServicesUpdated);
  }

  broadcastEncounterServicesVerified() {
    this._eventService.broadcast(BroadcastEventTypes.EncounterServicesVerified);
  }

  broadcastEncounterContextDeleted() {
    this._eventService.broadcast(BroadcastEventTypes.EncounterContextsDeleted);
  }

  setEncounterMedicalHistoryCache(history: EncounterMedicalHistory[]) {
    this.applicationCache.encounterMedicalHistory = history;
    this._eventService.broadcast(
      BroadcastEventTypes.EncounterMedicalHistoryUpdated
    );
  }

  updateEncounterMedicalCache(history: EncounterMedicalHistory) {
    const matchingRecord = this.applicationCache.encounterMedicalHistory.find(
      (x) => x.id === history.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, history);
    } else {
      this.applicationCache.encounterMedicalHistory.push({ ...history });
    }
    this._eventService.broadcast(
      BroadcastEventTypes.EncounterMedicalHistoryUpdated
    );
  }

  setEncounterSocialHistoryCache(history: EncounterSocialHistory[]) {
    this.applicationCache.encounterSocialHistory = history;
    this._eventService.broadcast(
      BroadcastEventTypes.EncounterSocialHistoryUpdated
    );
  }

  updateEncounterSocialCache(history: EncounterSocialHistory) {
    const matchingRecord = this.applicationCache.encounterSocialHistory.find(
      (x) => x.id === history.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, history);
    } else {
      this.applicationCache.encounterSocialHistory.push({ ...history });
    }
    this._eventService.broadcast(
      BroadcastEventTypes.EncounterSocialHistoryUpdated
    );
  }

  setEncounterFamilyHistoryCache(history: EncounterFamilyHistory[]) {
    this.applicationCache.encounterFamilyHistory = history;
    this._eventService.broadcast(
      BroadcastEventTypes.EncounterFamilyHistoryUpdated
    );
  }

  updateEncounterFamilyCache(history: EncounterFamilyHistory) {
    const matchingRecord = this.applicationCache.encounterFamilyHistory.find(
      (x) => x.id === history.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, history);
    } else {
      this.applicationCache.encounterFamilyHistory.push({ ...history });
    }
    this._eventService.broadcast(
      BroadcastEventTypes.EncounterFamilyHistoryUpdated
    );
  }

  setEncounterMedicationCache(medications: Medication[]) {
    const cache = this.resolveWriteCache();
    cache.encounterMedications = filterVisibleEncounterMedications(medications);
    this.broadcastCacheChange(BroadcastEventTypes.EncounterMedicationsUpdated);
  }

  setEncounterClinicalOrdersCache(rows: unknown[]) {
    const cache = this.resolveWriteCache();
    cache.encounterClinicalOrders = rows ?? [];
  }

  updateEncounterMedicationCache(medication: Medication, broadcast: boolean) {
    // Ensure encounterMedications array exists
    if (!this.applicationCache.encounterMedications) {
      this.applicationCache.encounterMedications = [];
    }

    if (isCancelledEncounterMedication(medication)) {
      this.applicationCache.encounterMedications =
        this.applicationCache.encounterMedications.filter(
          (x) => x.id !== medication.id,
        );
      if (broadcast) {
        this._eventService.broadcast(
          BroadcastEventTypes.EncounterMedicationsUpdated,
        );
      }
      return;
    }

    const matchingRecord = this.applicationCache.encounterMedications.find(
      (x) => x.id === medication.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, medication);
    } else {
      this.applicationCache.encounterMedications.push({ ...medication });
    }
    if (!broadcast) {
      return;
    }
    this._eventService.broadcast(
      BroadcastEventTypes.EncounterMedicationsUpdated
    );
  }

  setRosSummaryCache(rosSummary: RosSummary) {
    this.applicationCache.rosSummary = rosSummary;
    this._eventService.broadcast(BroadcastEventTypes.RosSummaryUpdated);
  }

  broadcastEncounterMedicationUpdated() {
    this._eventService.broadcast(
      BroadcastEventTypes.EncounterMedicationsUpdated
    );
  }

  setEncounterSurgicalHistoryCache(history: EncounterSurgicalHistory[]) {
    this.applicationCache.encounterSurgicalHistory = history;
    this._eventService.broadcast(
      BroadcastEventTypes.EncounterSurgicalHistoryUpdated
    );
  }

  updateEncounterSurgicalCache(history: EncounterSurgicalHistory) {
    const matchingRecord = this.applicationCache.encounterSurgicalHistory.find(
      (x) => x.id === history.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, history);
    } else {
      this.applicationCache.encounterSurgicalHistory.push({ ...history });
    }
    this._eventService.broadcast(
      BroadcastEventTypes.EncounterSurgicalHistoryUpdated
    );
  }

  setEncounterSoapNoteCache(soapNote: SoapNote) {
    this.applicationCache.encounterSoapNote = soapNote;
    this._eventService.broadcast(BroadcastEventTypes.EncounterSoapNoteUpdated);
  }

  clearEncounterSoapNoteCache(encounterId: string): void {
    this.applicationCache.encounterSoapNote = {
      encounterId,
      note: "",
    };
    this.setApiStatus(ApiStatus.Success, EncounterApis.SoapNote);
    this._eventService.broadcast(BroadcastEventTypes.EncounterSoapNoteUpdated);
  }

  setEncounterContextCache(context: Context) {
    // Procedure-room contexts live in `procedureRoomContexts`; PATCH responses must merge there
    // so UI bound to those objects updates (and we avoid pushing procedure rows into encounterContexts).
    const procList = this.applicationCache.procedureRoomContexts || [];
    const procMatch = procList.find((x) => x.id === context.id);
    if (procMatch) {
      Object.assign(procMatch, context);
      this._eventService.broadcast(
        BroadcastEventTypes.ProcedureRoomContextsUpdated
      );
      return;
    }

    // Always update the contexts list cache for every context (including Plan Summary and Assessment Summary).
    // This keeps applicationCache.encounterContexts in sync whenever a context is saved/updated via API.
    const matchingRecord = this.applicationCache.encounterContexts.find(
      (x) => x.id === context.id
    );

    if (matchingRecord) {
      Object.assign(matchingRecord, context);
    } else {
      const title = (context.title || '').trim();
      // When API returns a new version (new id), replace existing context with same title to avoid duplicate entries (HPI, ROS, History, Functional limitation, etc.).
      const existingByTitle = title
        ? this.applicationCache.encounterContexts.find((x) => (x.title || '').trim() === title)
        : undefined;
      if (existingByTitle) {
        Object.assign(existingByTitle, context);
      } else {
        this.applicationCache.encounterContexts.push({ ...context });
      }
    }

    // Plan Summary and Assessment Summary broadcast their own events; skip generic broadcast to avoid full context list refresh
    const title = context.title || '';
    if (title !== 'Plan Summary' && title !== 'Assessment Summary') {
      this.broadcastContextsUpdated();
    }
  }

  broadcastContextsUpdated(encounterId?: string) {
    const id =
      (encounterId || this.applicationCache.appointment?.id || "").trim() ||
      undefined;
    this._eventService.broadcast(
      BroadcastEventTypes.EncounterContextsUpdated,
      id ? { encounterId: id } : {}
    );
  }

  setTasksCache(task: Task) {
    const matchingRecord = this.applicationCache.tasks.find(
      (x) => x.id === task.id
    );

    if (matchingRecord) {
      matchingRecord.status = task.status
      // Object.assign(matchingRecord, task);
    } else {
      this.applicationCache.tasks.push({ ...task });
    }
    this.broadcastTasksUpdated();
  }

  /**
   * Set all tasks in cache at once
   * Used when loading all tasks from the server
   */
  setAllTasksCache(tasks: Task[]) {
    this.applicationCache.tasks = tasks || [];
    this.broadcastTasksUpdated();
  }

  /**
   * Clear all tasks from cache
   */
  clearTasksCache() {
    this.applicationCache.tasks = [];
    this.broadcastTasksUpdated();
  }

  broadcastTasksUpdated() {
    this._eventService.broadcast(BroadcastEventTypes.TasksUpdated);
  }

  setTriageQuestionsCache(triageQuestions: Record<string, {
    id: string;
    question: string;
    category: string;
    comment: string;
    isSelected: 'Positive' | 'Negative' | 'None';
  }[]>) {
    this.applicationCache.triageQuestions = triageQuestions;
  }

  clearSelection() {
    this._searchPreviewOverlay = null;
    this._mainApplicationCache = new ApplicationCache();
    this.apiStatuses = new Map<string, ApiStatus>([
      [ReferralApis.Patient, ApiStatus.Pending],
      [ReferralApis.Documents, ApiStatus.Pending],
      [ReferralApis.PriorAuth, ApiStatus.Pending],
      [ReferralApis.Coverages, ApiStatus.Pending],
      [ReferralApis.ChecklistFlags, ApiStatus.Pending],
      [ReferralApis.ChecklistQuestions, ApiStatus.Pending],
      [ReferralApis.ChecklistValues, ApiStatus.Pending],
      [ReferralApis.Labs, ApiStatus.Pending],
      [ReferralApis.ChiefComplaint, ApiStatus.Pending],
      [ProcedureRoomApis.ProcedureRoomContexts, ApiStatus.Pending],
      [ProcedureRoomApis.Patient, ApiStatus.Pending],
      [ProcedureRoomApis.NeurovascularAssessment, ApiStatus.Pending],
      [ProcedureRoomApis.SnacksAndMeals, ApiStatus.Pending],
      [ProcedureRoomApis.Vitals, ApiStatus.Pending],
      [ProcedureRoomApis.BloodGlucose, ApiStatus.Pending],
      [ProcedureRoomApis.DispenseSupplies, ApiStatus.Pending],
      [ProcedureRoomApis.LeadEKG, ApiStatus.Pending],
      [ProcedureRoomApis.ModifiedAldreteScoring, ApiStatus.Pending],
      [ProcedureRoomApis.MedicationReconciliation, ApiStatus.Pending],
      [ProcedureRoomApis.PreProcedureAssessment, ApiStatus.Pending],
      [ProcedureRoomApis.AdministerMedication, ApiStatus.Pending],
      [ProcedureRoomApis.AccessSite, ApiStatus.Pending],
      [ProcedureRoomApis.Act, ApiStatus.Pending],
      [ProcedureRoomApis.ProcedureSummary, ApiStatus.Pending],
      [ProcedureRoomApis.ProcedureTimeout, ApiStatus.Pending],
      [ProcedureRoomApis.ICDCodes, ApiStatus.Pending],
      [ProcedureRoomApis.CPTCodes, ApiStatus.Pending],
      [ProcedureRoomApis.Discharge, ApiStatus.Pending],
      [ReferralApis.PreAnesthsiaSummary, ApiStatus.Pending],
      [EncounterApis.Problems, ApiStatus.Pending],
      [EncounterApis.HpiQuestions, ApiStatus.Pending],
      [EncounterApis.HpiSummary, ApiStatus.Pending],
      [EncounterApis.Encounter, ApiStatus.Pending],
      [EncounterApis.Medications, ApiStatus.Pending],
      [EncounterApis.Services, ApiStatus.Pending],
      [EncounterApis.SoapNote, ApiStatus.Pending],
      [EncounterApis.RosSummary, ApiStatus.Pending],
    ]);
  }

  get apiBaseUrl(): string {
    return environment.apiBaseUrl;
  }

  //=================================suggested services cache management ===========================================

  setSuggestedServicesCache(suggestedServices: any[]) {
    this.applicationCache.suggestedServices = [...suggestedServices];
    this._eventService.broadcast(BroadcastEventTypes.SuggestedServicesUpdated);
  }

  updateSuggestedServicesCache(suggestedServices: any[], isNew: boolean = false) {
    if (isNew) {
      this.applicationCache.suggestedServices.push(...suggestedServices);
    } else {
      suggestedServices.forEach((service: any) => {
        const matchingRecord = this.applicationCache.suggestedServices.find(
          (x) => x.id === service.id
        );

        if (matchingRecord) {
          Object.assign(matchingRecord, service);
        } else {
          this.applicationCache.suggestedServices.push({ ...service });
        }
      });
    }
    this._eventService.broadcast(BroadcastEventTypes.SuggestedServicesUpdated);
  }

  clearSuggestedServicesCache() {
    this.applicationCache.suggestedServices = [];
    this._eventService.broadcast(BroadcastEventTypes.SuggestedServicesUpdated);
  }

  //=================================suggested problems cache management ===========================================

  setSuggestedProblemsCache(suggestedProblems: any[]) {
    this.applicationCache.suggestedProblems = [...suggestedProblems];
    this._eventService.broadcast(BroadcastEventTypes.SuggestedProblemsUpdated);
  }

  updateSuggestedProblemsCache(suggestedProblems: any[], isNew: boolean = false) {
    if (isNew) {
      this.applicationCache.suggestedProblems.push(...suggestedProblems);
    } else {
      suggestedProblems.forEach((problem: any) => {
        const matchingRecord = this.applicationCache.suggestedProblems.find(
          (x) => x.id === problem.id
        );

        if (matchingRecord) {
          Object.assign(matchingRecord, problem);
        } else {
          this.applicationCache.suggestedProblems.push({ ...problem });
        }
      });
    }
    this._eventService.broadcast(BroadcastEventTypes.SuggestedProblemsUpdated);
  }

  clearSuggestedProblemsCache() {
    this.applicationCache.suggestedProblems = [];
    this._eventService.broadcast(BroadcastEventTypes.SuggestedProblemsUpdated);
  }

  //=================================recent appointments cache management ===========================================

  addToRecentAppointments(appointment: any) {
    // Check if appointment already exists in recent list
    const existingIndex = this.applicationCache.recentAppointments.findIndex(
      app => app.encounterId === appointment.encounterId || app.id === appointment.id
    );

    if (existingIndex !== -1) {
      // Remove existing appointment from its current position
      this.applicationCache.recentAppointments.splice(existingIndex, 1);
    }

    // Add appointment to the beginning of the list
    this.applicationCache.recentAppointments.unshift(appointment);

    // Keep only the latest 5 appointments
    if (this.applicationCache.recentAppointments.length > 5) {
      this.applicationCache.recentAppointments = this.applicationCache.recentAppointments.slice(0, 5);
    }

    this._eventService.broadcast(BroadcastEventTypes.RecentAppointmentsUpdated);
  }

  setRecentAppointments(appointments: any[]) {
    // Clear current cache
    this.applicationCache.recentAppointments = [];
    
    // Add each appointment directly to avoid duplicate checking issues
    appointments.forEach(appointment => {
      this.applicationCache.recentAppointments.push(appointment);
    });
    
    // Keep only the latest 5 appointments
    if (this.applicationCache.recentAppointments.length > 5) {
      this.applicationCache.recentAppointments = this.applicationCache.recentAppointments.slice(0, 5);
    }
    
    this._eventService.broadcast(BroadcastEventTypes.RecentAppointmentsUpdated);
  }

  getRecentAppointments(): any[] {
    return this.applicationCache.recentAppointments;
  }

  clearRecentAppointments() {
    this.applicationCache.recentAppointments = [];
    this._eventService.broadcast(BroadcastEventTypes.RecentAppointmentsUpdated);
  }

  // PQRSTA Questions Cache Management
  setPqrstaQuestionsCache(questions: any[]) {
    this.applicationCache.pqrstaQuestions = questions;
    this._eventService.broadcast(BroadcastEventTypes.PqrstaQuestionsUpdated);
  }

  updatePqrstaQuestionsCache(questions: any[]) {
    this.applicationCache.pqrstaQuestions = questions;
    this._eventService.broadcast(BroadcastEventTypes.PqrstaQuestionsUpdated);
  }

  clearPqrstaQuestionsCache() {
    this.applicationCache.pqrstaQuestions = [];
    this._eventService.broadcast(BroadcastEventTypes.PqrstaQuestionsUpdated);
  }

  // OPQRST Questions Cache Management
  setOpqrstQuestionsCache(questions: any[]) {
    this.applicationCache.opqrstQuestions = questions;
    this._eventService.broadcast(BroadcastEventTypes.OpqrstQuestionsUpdated);
  }

  updateOpqrstQuestionsCache(questions: any[]) {
    this.applicationCache.opqrstQuestions = questions;
    this._eventService.broadcast(BroadcastEventTypes.OpqrstQuestionsUpdated);
  }

  clearOpqrstQuestionsCache() {
    this.applicationCache.opqrstQuestions = [];
    this._eventService.broadcast(BroadcastEventTypes.OpqrstQuestionsUpdated);
  }

  // History Questions Cache Management
  setHistoryQuestionsCache(questions: any[]) {
    this.applicationCache.historyQuestions = questions;
    this._eventService.broadcast(BroadcastEventTypes.HistoryQuestionsUpdated);
  }

  updateHistoryQuestionsCache(questions: any[]) {
    this.applicationCache.historyQuestions = questions;
    this._eventService.broadcast(BroadcastEventTypes.HistoryQuestionsUpdated);
  }

  clearHistoryQuestionsCache() {
    this.applicationCache.historyQuestions = [];
    this._eventService.broadcast(BroadcastEventTypes.HistoryQuestionsUpdated);
  }

  // Templates Cache Management
  setTemplatesCache(templates: any[]) {
    this.applicationCache.templates = templates;
    this._eventService.broadcast(BroadcastEventTypes.TemplatesUpdated);
  }

  updateTemplatesCache(templates: any[]) {
    this.applicationCache.templates = templates;
    this._eventService.broadcast(BroadcastEventTypes.TemplatesUpdated);
  }

  clearTemplatesCache() {
    this.applicationCache.templates = [];
    this._eventService.broadcast(BroadcastEventTypes.TemplatesUpdated);
  }

  // Prompts Cache Management
  setPromptsCache(prompts: any[]) {
    this.applicationCache.prompts = prompts;
    this._eventService.broadcast(BroadcastEventTypes.PromptsUpdated);
  }

  updatePromptsCache(prompts: any[]) {
    this.applicationCache.prompts = prompts;
    this._eventService.broadcast(BroadcastEventTypes.PromptsUpdated);
  }

  clearPromptsCache() {
    this.applicationCache.prompts = [];
    this._eventService.broadcast(BroadcastEventTypes.PromptsUpdated);
  }
}
